import {Avatar, Button, Dropdown, Input, Select, Space, Tooltip, Modal, Tag, AutoComplete, Badge} from "antd";
import {
    BellOutlined, LogoutOutlined,
    MenuFoldOutlined, MenuUnfoldOutlined, MoonOutlined,
    QuestionCircleOutlined, SearchOutlined, SettingOutlined,
    SunOutlined, UserOutlined
} from "@ant-design/icons";
import {Layout, Typography} from "antd";
import {ENV, ROLE_OPTIONS, SERVER_CONFIG, getServerOptions} from "../shared/utils/enum";
import {useContext, useMemo, useState} from "react";
import {AuthContext} from "../shared/contexts/authContext";
import {useLocation, useNavigate} from "react-router-dom";
import {DarkModeContext} from "../shared/contexts/darkModeContext";
import {useLogout} from "../shared/hooks/useAuth";

const {Header} = Layout;
const {Text, Title} = Typography;

const pathMap = {
    'player': '유저 정보',
    'playerCopy': '유저 정보 복사',
    'event': '이벤트 관리',
    'notice': '공지사항',
    'coupon': '쿠폰 관리',
    'message': 'Push 알림',
    'campaign': '전체 우편',
    'logs': '로그 관리',
    'logs/payment': '결제 로그',
    'logs/mail': '우편 로그',
    'logs/item': '재화 로그',
    'logs/admin': '관리자 로그',
    'logs/gachaLog': '가챠 로그',
    'logs/shopLog': '아이템 구입 로그',
    'logs/packageLog': '패스/체인딜',
    'logs/changeBadge': '배지 교환',
    'logs/rewardBadge': '배지 보상',
    'logs/mergeEventRoundClearLog': '라운드 클리어 로그',
    'shopPackage': '상점 관리',
    'itemProbability': '확률표',
    'inApp': '인앱상품',
    'csv': 'CSV 관리',
    'serverInfo': '서버 정보',
    'country': '국가/언어',
    'admin': '관리자',
    'statistics': '통계',
    'statistics/user_metric': '유저 통계',
    'statistics/retention': '리텐션',
    'statistics/analytics': '매출 통계',
    'statistics/package': '패키지 통계',
    'hack': '해킹 감지',
    'refund': '환불',
    'receiptFinder': '영수증 찾기(애플)',
    'timestampConverter': '타임스탬프 변환기',
    'json': 'JSON 파서',
};

const HeaderBar = ({collapsed, setCollapsed, sideWidth}) => {
    const {currentUser, changeEnv} = useContext(AuthContext);
    const {darkMode, toggle} = useContext(DarkModeContext);
    const location = useLocation();
    const navigate = useNavigate();
    const {mutate: logout} = useLogout();

    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [isComposing, setIsComposing] = useState(false);
    const [hasSearchUpdate, setHasSearchUpdate] = useState(() => {
        return !localStorage.getItem('searchUpdateChecked');
    });

    const menuKeys = useMemo(() => Object.keys(pathMap), []);
    // 메뉴 추천 리스트
    const menuOptions = useMemo(() => menuKeys.map(key => ({
        value: key,
        label: (
            <span>
                <b>{pathMap[key]}</b>
                <Tag color="blue" style={{marginLeft: 8}}>메뉴</Tag>
            </span>
        )
    })), [menuKeys]);

    // filterOption: label(한글) 또는 key(영문)에 입력값이 포함될 때만 추천
    const filterOption = (input, option) => {
        const labelText = pathMap[option.value] || '';
        return (
            (option.value && option.value.toLowerCase().includes(input.toLowerCase())) ||
            (labelText && labelText.includes(input))
        );
    };

    // 메뉴 추천 선택 시 이동
    const handleAutoCompleteSelect = (key) => {
        if (key) {
            setSearchModalOpen(false);
            setSearchValue("");
            navigate(`/${key}`);
        }
    };

    const handleSearch = () => {
        if (!searchValue) return;

        const filteredOptions = menuOptions.filter(opt => filterOption(searchValue, opt));
        if (filteredOptions.length > 0) {
            setSearchModalOpen(false);
            setSearchValue("");
            navigate(`/${filteredOptions[0].value}`);
            return;
        }
        // PID로 이동 (GoToUserInfoButton과 동일하게 쿼리스트링 적용)
        setSearchModalOpen(false);
        const pid = searchValue.trim();
        const queryParams = new URLSearchParams({
            type: 'pid',
            value: pid,
            target: currentUser?.target || ''
        }).toString();
        setSearchValue("");
        navigate(`/player?${queryParams}`);
    };

    const handleModalOpen = () => {
        setSearchValue("");
        setSearchModalOpen(true);
        if (hasSearchUpdate) {
            localStorage.setItem('searchUpdateChecked', 'true');
            setHasSearchUpdate(false);
        }
    };

    const handleLogout = async () => {
        logout({}, {
            onSuccess: () => {
            },
            onError: (error) => {
                console.error(error);
            }
        });
    };

    const handleChangeEnv = async (value) => {
        await changeEnv(value);
        await new Promise(resolve => setTimeout(resolve, 150));
        window.location.reload();
    };

    const getPageTitle = () => {
        const pathName = location.pathname.split('/')[1];
        return pathMap[pathName] || 'Dashboard';
    };

    const userMenuItems = [
        {key: '1', icon: <UserOutlined/>, label: '프로필'},
        {key: '2', icon: <SettingOutlined/>, label: '계정 설정'},
        {type: 'divider'},
        {key: '3', icon: <LogoutOutlined/>, label: '로그아웃', danger: true, onClick: handleLogout},
    ];

    const getRoleText = (role) => {
        const found = ROLE_OPTIONS.find(option => option.value === role);
        return found ? found.label : role;
    };

    return (
        <Header
            className="layout-header"
            style={{
                background: darkMode ? 'rgba(20, 20, 20, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                left: sideWidth,
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(20px)',
                borderBottom: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`
            }}
        >
            <div className="header-left">
                <Tooltip title={collapsed ? "메뉴 펼치기" : "메뉴 접기"} placement="bottom">
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
                        onClick={() => setCollapsed(!collapsed)}
                        className="collapse-button"
                        size="large"
                    />
                </Tooltip>
                <div className="env-selector-container">
                    <Text style={{
                        fontSize: '12px',
                        color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        marginRight: '8px',
                        fontWeight: 500
                    }}>
                        서버
                    </Text>
                    <Select
                        value={currentUser?.target}
                        onChange={handleChangeEnv}
                        size="small"
                        style={{width: 200, fontWeight: "600"}}
                        popupMatchSelectWidth={false}
                        dropdownStyle={{minWidth: 200}}
                    >
                        {getServerOptions().map(env => {
                            const config = SERVER_CONFIG[env];
                            return (
                                <Select.Option key={env} value={env}>
                                    <Space>
                                        <div style={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: config.color
                                        }}/>
                                        <span style={env === ENV.PRODUCTION ? {color: config.color, fontWeight: 'bold'} : {}}>
                                            {config.fullLabel}
                                        </span>
                                    </Space>
                                </Select.Option>
                            );
                        })}
                    </Select>
                </div>
                <div className="page-title-section">
                    <Title level={4} style={{margin: 0, color: darkMode ? '#fff' : '#1a1a1a', fontWeight: 600}}>
                        {getPageTitle()}
                    </Title>
                    <Text type="secondary" style={{fontSize: '12px'}}>
                        {new Date().toLocaleDateString('ko-KR', {year: 'numeric', month: 'long', day: 'numeric'})}
                    </Text>
                </div>
            </div>
            <div className="header-right">
                <Space size="middle">
                    <Tooltip title={'전체 검색'} placement="bottom">
                        <Badge dot={hasSearchUpdate} offset={[-2, 2]}>
                            <Button
                                type="text"
                                icon={<SearchOutlined/>}
                                className="header-action-btn"
                                size="large"
                                onClick={handleModalOpen}
                            />
                        </Badge>
                    </Tooltip>
                    <Modal
                        open={searchModalOpen}
                        onCancel={() => setSearchModalOpen(false)}
                        onOk={handleSearch}
                        okText="이동"
                        cancelText="취소"
                        title="메뉴 또는 유저 PID 검색"
                        width={400}
                        footer={(_, {OkBtn, CancelBtn}) => (
                            <Space>
                                <CancelBtn/>
                                <OkBtn/>
                            </Space>
                        )}
                        afterOpenChange={open => {
                            if (open) setTimeout(() => {
                                document.getElementById('search-autocomplete')?.focus();
                            }, 100);
                        }}
                    >
                        <AutoComplete
                            id="search-autocomplete"
                            style={{width: '100%'}}
                            options={menuOptions}
                            value={searchValue}
                            onChange={setSearchValue}
                            onSelect={handleAutoCompleteSelect}
                            filterOption={filterOption}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !isComposing) handleSearch();
                            }}
                            placeholder="메뉴명 또는 유저 PID 입력"
                            allowClear
                            notFoundContent={<span>PID로 유저정보 이동</span>}
                        >
                            <Input
                                onCompositionStart={() => setIsComposing(true)}
                                onCompositionEnd={() => setIsComposing(false)}
                                suffix={
                                    <Tag
                                        color={searchValue && menuOptions.filter(opt => filterOption(searchValue, opt)).length > 0 ? "blue" : "green"}
                                        style={{
                                            visibility: searchValue ? 'visible' : 'hidden',
                                            transition: 'none'
                                        }}
                                    >
                                        {searchValue
                                            ? (menuOptions.filter(opt => filterOption(searchValue, opt)).length > 0 ? "메뉴" : "유저찾기")
                                            : "_"}
                                    </Tag>
                                }
                            />
                        </AutoComplete>
                        <div style={{marginTop: 8, fontSize: 12, color: '#888'}}>
                            한글 입력이 깨질 경우 새로고침 후 시도해 주세요.
                        </div>
                    </Modal>
                    <Tooltip title="도움말" placement="bottom">
                        <Button type="text" icon={<QuestionCircleOutlined/>} className="header-action-btn"
                                size="large"/>
                    </Tooltip>
                    <Tooltip title={darkMode ? "라이트 모드" : "다크 모드"} placement="bottom">
                        <Button
                            type="text"
                            icon={darkMode ? <SunOutlined/> : <MoonOutlined/>}
                            onClick={toggle}
                            className="header-action-btn theme-toggle"
                            size="large"
                        />
                    </Tooltip>
                    <Tooltip title="알림" placement="bottom">
                        <Button
                            type="text"
                            icon={<BellOutlined/>}
                            size="large"
                            className="header-action-btn notification-button"
                        />
                    </Tooltip>
                    <Dropdown
                        menu={{items: userMenuItems}}
                        placement="bottomRight"
                        arrow
                        trigger={['click']}
                    >
                        <Button
                            type="text"
                            className="user-profile-btn"
                            style={{height: 'auto', padding: '8px 12px', borderRadius: '8px'}}
                        >
                            <Space align="center" size="small">
                                <Avatar
                                    src="https://img.utdstc.com/icon/e7b/286/e7b2860763114b21dc1cba6b59c619fc865bf7d8016459b51c5602d63cf62de8:200"
                                    size="default"
                                />
                                <div style={{textAlign: 'left'}}>
                                    <Text strong style={{
                                        color: darkMode ? '#fff' : '#1a1a1a',
                                        fontSize: '13px',
                                        display: 'block'
                                    }}>
                                        {currentUser?.name}
                                    </Text>
                                    <Text type="secondary" style={{fontSize: '11px'}}>
                                        {getRoleText(currentUser.role)}
                                    </Text>
                                </div>
                            </Space>
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        </Header>
    )
}

export default HeaderBar;
